// titleBarDeck15.js — PURE, framework-free core for the Title Bar block's
// DECK-15 DELTA controls (the ~60% of the canonical editor the shipped
// "smallest block" scope left DESIGNED-not-built).
//
// Deck source of truth (verbatim control groups read off it):
//   C:\iYuga\design-handoff\concepts\builder-blocks\15-titlebar-block-editor.html
//     Size        · Height  S · M · L · Custom -> 52 px
//                 · Width   Full · 75% · 50%
//     Text overlay· Text size S · M · L · Custom -> 16 px
//                 · Corner rounding None · S · M · Full -> 8 px
//                 · Text position (3x3 grid, deck shows "Middle Left")
//                 · Overlay  Block behind text · Shadow behind text · Whole bar
//                 · Darken <-> Lighten -> -35
//
// Everything here is a pure decision (value -> px / CSS / color) so it is unit
// tested with no DOM, exactly like titleBarLogic.js. The React view turns these
// decisions into markup; the settings pane writes the props.
//
// NOT in this unit (deliberately, each its own build unit): the crop-to-bar drag
// MARQUEE tool and the Patterns background tab.
//
// Radii come from the design TOKENS (radius.none/sm/md/pill) — no magic numbers.

import { radius } from "../../../../theme/tokens";
import { relativeLuminance, INK, LIGHT, SCRIM_SHADOW } from "./titleBarLogic";

// ── Corner rounding (None · S · M ✓ · Full) — deck "Corner rounding … M = 8 px".
// The shipped block hard-codes BAR_CORNER_RADIUS = 2; this turns it into the
// deck's control, mapped onto the radius token scale.
export const CORNER_ROUNDINGS = ["none", "s", "m", "full"];
export const ROUNDING_PX = {
  none: radius.none, // 0
  s: radius.sm, // 4
  m: radius.md, // 8  (deck default M)
  full: radius.pill, // 9999 -> a fully-rounded (pill) band
};
export const roundingToPx = (r) =>
  Object.prototype.hasOwnProperty.call(ROUNDING_PX, r)
    ? ROUNDING_PX[r]
    : ROUNDING_PX.m;

// ── Bar width (Full ✓ · 75% · 50%) — deck Size "Width Full · 75% · 50%".
// The live band is always full width today.
export const BAR_WIDTHS = ["full", "75", "50"];
const WIDTH_CSS = { full: "100%", 75: "75%", 50: "50%" };
export const widthToCss = (w) =>
  Object.prototype.hasOwnProperty.call(WIDTH_CSS, w) ? WIDTH_CSS[w] : WIDTH_CSS.full;

// ── Text position (3x3) — the built Left/Center/Right alignment is the 1-D
// subset of the deck's 2-D grid. Values match the SHARED <PosGrid> control's
// vocabulary exactly ("top-left" … "bottom-right") so no new picker is invented.
export const POSITIONS = [
  "top-left",
  "top-center",
  "top-right",
  "middle-left",
  "middle-center",
  "middle-right",
  "bottom-left",
  "bottom-center",
  "bottom-right",
];
const VERTICAL_ALIGN = { top: "flex-start", middle: "center", bottom: "flex-end" };
const HORIZONTAL_JUSTIFY = { left: "flex-start", center: "center", right: "flex-end" };

/** Split a PosGrid value into its two axes, falling back to the deck default. */
export const splitPosition = (pos) => {
  const value = POSITIONS.includes(pos) ? pos : "middle-center";
  const [vertical, horizontal] = value.split("-");
  return { vertical, horizontal };
};

/**
 * Map a 3x3 position onto the band's flex + text alignment.
 * @param {string} pos e.g. "middle-left"
 * @returns {{alignItems:string, justifyContent:string, textAlign:string}}
 */
export const positionToStyle = (pos) => {
  const { vertical, horizontal } = splitPosition(pos);
  return {
    alignItems: VERTICAL_ALIGN[vertical],
    justifyContent: HORIZONTAL_JUSTIFY[horizontal],
    textAlign: horizontal,
  };
};

/** The 1-D alignment that corresponds to a 3x3 position (back-compat bridge). */
export const positionToAlign = (pos) => splitPosition(pos).horizontal;

/** The 3x3 position a legacy 1-D alignment corresponds to (migration bridge). */
export const alignToPosition = (align) =>
  `middle-${["left", "center", "right"].includes(align) ? align : "center"}`;

// ── "Custom" escape hatch on Height AND Text size — deck shows both as a fourth
// choice with a live px readout (52 px / 16 px). Values are clamped to sane
// component geometry so a stray number can never break the band.
export const CUSTOM = "custom";
export const CUSTOM_HEIGHT_RANGE = { min: 32, max: 200, fallback: 52 };
export const CUSTOM_SIZE_RANGE = { min: 10, max: 48, fallback: 16 };

const clampTo = (value, { min, max, fallback }) => {
  // An unset field is NOT "0" — it is the deck default (Number(null) === 0 would
  // otherwise clamp an untouched control to the minimum).
  if (value === null || value === undefined || value === "") return fallback;
  const n = Number(value);
  if (!Number.isFinite(n)) return fallback;
  return Math.min(max, Math.max(min, Math.round(n)));
};
export const clampCustomHeight = (value) => clampTo(value, CUSTOM_HEIGHT_RANGE);
export const clampCustomSize = (value) => clampTo(value, CUSTOM_SIZE_RANGE);

// ── Overlay scope (Block behind text · Shadow behind text · Whole bar) +
// Darken <-> Lighten. Deck: "Block behind text (block rounds with Corner
// rounding, darkened -35) · Shadow behind text (soft text shadow, no block) ·
// Whole bar (the whole rectangle tints)."
export const OVERLAY_SCOPES = ["block", "shadow", "bar"];
export const OVERLAY_NONE = "none"; // shipped behavior: no overlay at all
export const ADJUST_RANGE = { min: -100, max: 100 };
// A full-strength tint stops at 0.8 alpha so a bar's own background is never
// completely erased (the author still sees what they picked).
const MAX_TINT_ALPHA = 0.8;

/** Clamp the Darken<->Lighten slider (negative = darken, positive = lighten). */
export const clampAdjust = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return 0;
  return Math.min(ADJUST_RANGE.max, Math.max(ADJUST_RANGE.min, Math.round(n)));
};

const tintRgba = (adjust) => {
  const a = (Math.abs(adjust) / 100) * MAX_TINT_ALPHA;
  if (a === 0) return null;
  const channel = adjust < 0 ? "0,0,0" : "255,255,255";
  return `rgba(${channel},${a.toFixed(3)})`;
};

/**
 * Resolve the overlay decision for a scope + adjust pair.
 *
 *  - "bar"    -> the whole rectangle tints (a tint layer over the band).
 *  - "block"  -> only a block BEHIND THE TEXT tints; it rounds with the bar's
 *                own Corner rounding (deck note), so the caller passes it on.
 *  - "shadow" -> no block at all; a soft shadow behind the text, dark when
 *                darkening and light when lightening.
 *  - "none" / adjust 0 -> nothing (the shipped, byte-identical behavior).
 *
 * @param {string} scope one of OVERLAY_SCOPES (or "none")
 * @param {number} adjust -100..100
 * @returns {{barTint:string|null, textBlock:string|null, textShadow:string|null}}
 */
export const resolveOverlay = (scope, adjust) => {
  const none = { barTint: null, textBlock: null, textShadow: null };
  if (!OVERLAY_SCOPES.includes(scope)) return none;
  const value = clampAdjust(adjust);
  if (value === 0) return none;
  if (scope === "shadow") {
    const strength = (Math.abs(value) / 100).toFixed(3);
    return {
      ...none,
      textShadow:
        value < 0
          ? `0 1px 4px rgba(18,48,81,${strength})`
          : `0 1px 4px rgba(255,255,255,${strength})`,
    };
  }
  const tint = tintRgba(value);
  return scope === "bar"
    ? { ...none, barTint: tint }
    : { ...none, textBlock: tint };
};

/**
 * Blend an overlay tint onto a solid hex so AUTO text-contrast stays HONEST —
 * darkening a light bar to -80 must flip the title to white, which it cannot do
 * if the contrast check still samples the un-darkened color.
 *
 * @param {string|null} hex the bar's solid color (null for image/gradient fills)
 * @param {number} adjust -100..100
 * @returns {string|null} the blended `#rrggbb`, or null when unsamplable
 */
export const blendAdjust = (hex, adjust) => {
  const value = clampAdjust(adjust);
  if (relativeLuminance(hex) === null) return null;
  if (value === 0) return hex;
  let h = hex.trim().replace(/^#/, "");
  if (h.length === 3)
    h = h
      .split("")
      .map((c) => c + c)
      .join("");
  const alpha = (Math.abs(value) / 100) * MAX_TINT_ALPHA;
  const target = value < 0 ? 0 : 255;
  const mix = (pair) => {
    const c = parseInt(pair, 16);
    return Math.round(c * (1 - alpha) + target * alpha);
  };
  const toHex = (n) => n.toString(16).padStart(2, "0");
  return `#${toHex(mix(h.slice(0, 2)))}${toHex(mix(h.slice(2, 4)))}${toHex(
    mix(h.slice(4, 6))
  )}`;
};

/**
 * The solid color AUTO tone should sample, given the overlay in play. Only a
 * WHOLE-BAR tint changes what sits behind the text; a text block or a shadow
 * leaves the band color alone.
 */
export const contrastSolidFor = (scope, adjust, solidHex) =>
  scope === "bar" ? blendAdjust(solidHex, adjust) : solidHex;

export { INK, LIGHT, SCRIM_SHADOW };

const titleBarDeck15 = {
  CORNER_ROUNDINGS,
  ROUNDING_PX,
  roundingToPx,
  BAR_WIDTHS,
  widthToCss,
  POSITIONS,
  splitPosition,
  positionToStyle,
  positionToAlign,
  alignToPosition,
  CUSTOM,
  CUSTOM_HEIGHT_RANGE,
  CUSTOM_SIZE_RANGE,
  clampCustomHeight,
  clampCustomSize,
  OVERLAY_SCOPES,
  OVERLAY_NONE,
  ADJUST_RANGE,
  clampAdjust,
  resolveOverlay,
  blendAdjust,
  contrastSolidFor,
};

export default titleBarDeck15;
